// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later

import type { Core, Geo, Meta, RawRecord, Record_ } from './types';

const BASE = '/data';

/**
 * Data files are versioned by the extract's as-at date so a returning visitor
 * can never run a new bundle against cached old numbers. meta.json itself is
 * always revalidated, because it is what carries the version.
 */
let version = '';

async function getJson<T>(path: string, versioned = true): Promise<T> {
  const url = versioned && version ? `${BASE}/${path}?v=${version}` : `${BASE}/${path}`;
  const res = await fetch(url, versioned && version ? {} : { cache: 'no-cache' });
  if (!res.ok) throw new Error(`Could not load ${path} (HTTP ${res.status})`);
  return (await res.json()) as T;
}

let corePromise: Promise<{ core: Core; geo: Geo; meta: Meta }> | null = null;

export function loadCore(): Promise<{ core: Core; geo: Geo; meta: Meta }> {
  corePromise ??= (async () => {
    const meta = await getJson<Meta>('meta.json', false);
    version = meta.as_at_award_date;
    const [core, geo] = await Promise.all([getJson<Core>('core.json'), getJson<Geo>('geo.json')]);
    return { core, geo, meta };
  })();
  return corePromise;
}

const geoCache: { promise: Promise<GeoJSON.FeatureCollection> | null } = { promise: null };

export function loadBoundaries(): Promise<GeoJSON.FeatureCollection> {
  geoCache.promise ??= getJson<GeoJSON.FeatureCollection>('poa.geojson');
  return geoCache.promise;
}

const shardCache = new Map<string, Promise<RawRecord[]>>();

export function loadShard(letter: string): Promise<RawRecord[]> {
  const key = /^[A-Z]$/.test(letter) ? letter : '_';
  let p = shardCache.get(key);
  if (!p) {
    p = getJson<RawRecord[]>(`names/${key}.json`);
    shardCache.set(key, p);
  }
  return p;
}

const citeCache = new Map<number, Promise<Record<string, string>>>();

/**
 * Citations are bucketed by record id (not by name initial), so the drawer
 * fetches a few hundred KB rather than the 11 MB corpus.
 */
export function loadCitation(id: number, buckets: number): Promise<string | null> {
  const b = id % buckets;
  let p = citeCache.get(b);
  if (!p) {
    p = getJson<Record<string, string>>(`cites/${b}.json`);
    citeCache.set(b, p);
  }
  return p.then((m) => m[String(id)] ?? null);
}

/** Inflate a positional shard row using core.json's interned dictionaries. */
export function toRecord(r: RawRecord, d: Core['dicts']): Record_ {
  return {
    id: r[0],
    name: r[1],
    award: d.award[r[2]] ?? 'Unknown',
    year: r[3],
    suburb: r[4] >= 0 ? (d.suburb[r[4]] ?? '') : '',
    state: r[5] >= 0 ? (d.state[r[5]] ?? '') : '',
    postcode: r[6],
    postNominal: r[7],
    list: r[8] >= 0 ? (d.list[r[8]] ?? '') : '',
  };
}
